import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Loader2, CreditCard, Wallet, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtUSDExact } from "@/lib/format";

const POLL_MAX = 8;
const POLL_INTERVAL = 2000;

export default function Billing() {
  const { user, refresh } = useAuth();
  const [packs, setPacks] = useState([]);
  const [history, setHistory] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [processor, setProcessor] = useState("stripe"); // "stripe" | "paypal" | "paystack"
  const [proPlan, setProPlan] = useState("monthly"); // "monthly" | "annual"
  const [paypalCfg, setPaypalCfg] = useState({ enabled: false, mode: "sandbox" });
  const [paystackCfg, setPaystackCfg] = useState({ enabled: false, currency: "USD" });
  const [polling, setPolling] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadHistory = useCallback(() => {
    api.get("/billing/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/billing/packs").then(({ data }) => setPacks(data)).catch(() => {});
    api.get("/billing/paypal/config").then(({ data }) => setPaypalCfg(data)).catch(() => {});
    api.get("/billing/paystack/config").then(({ data }) => setPaystackCfg(data)).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  // Stripe redirect handler
  useEffect(() => {
    const canceled = searchParams.get("canceled");
    const sid = searchParams.get("session_id");
    if (canceled) {
      toast.error("Payment canceled");
      setSearchParams({});
      return;
    }
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const check = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Stripe payment successful — credit added");
          setPolling(false);
          setSearchParams({});
          await refresh();
          loadHistory();
          return;
        }
        if (data.status === "expired") {
          toast.error("Session expired");
          setPolling(false);
          setSearchParams({});
          return;
        }
      } catch (e) { /* retry */ }
      if (attempts >= POLL_MAX) {
        toast.message("Still processing — check history in a moment");
        setPolling(false);
        setSearchParams({});
        return;
      }
      setTimeout(check, POLL_INTERVAL);
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PayPal redirect handler: return_url adds ?paypal=success&token=<orderID>
  useEffect(() => {
    const paypalFlag = searchParams.get("paypal");
    const token = searchParams.get("token");
    if (paypalFlag === "cancel") {
      toast.error("PayPal payment canceled");
      setSearchParams({});
      return;
    }
    if (paypalFlag !== "success" || !token) return;
    setPolling(true);
    (async () => {
      try {
        const { data } = await api.post(`/billing/paypal/capture/${token}`);
        if (data.credited || data.status === "COMPLETED") {
          toast.success("PayPal payment successful — credit added");
        } else if (data.already_paid) {
          toast.message("PayPal order already captured");
        } else {
          toast.error(`PayPal status: ${data.status}`);
        }
        await refresh();
        loadHistory();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "PayPal capture failed");
      } finally {
        setPolling(false);
        setSearchParams({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Paystack redirect handler: callback adds ?paystack=success&reference=<ref>&trxref=<ref>
  useEffect(() => {
    const flag = searchParams.get("paystack");
    const reference = searchParams.get("reference") || searchParams.get("trxref");
    if (flag !== "success" || !reference) return;
    setPolling(true);
    (async () => {
      try {
        const { data } = await api.get(`/billing/paystack/verify/${reference}`);
        if (data.status === "success") {
          toast.success(
            data.already_paid
              ? "Paystack payment already credited"
              : "Paystack payment successful — credit added",
          );
          await refresh();
          loadHistory();
        } else {
          toast.error(`Paystack status: ${data.status}`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Paystack verification failed");
      } finally {
        setPolling(false);
        setSearchParams({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pro subscription redirect handler: ?pro_sub_session_id=<sid>
  useEffect(() => {
    const ssid = searchParams.get("pro_sub_session_id");
    if (!ssid) return;
    setPolling(true);
    (async () => {
      try {
        const { data } = await api.get(`/billing/pro/subscribe/status/${ssid}`);
        if (data.granted || data.payment_status === "paid") {
          toast.success("Pro subscription active — auto-renews");
          await refresh();
          loadHistory();
        } else {
          toast.error(`Subscription status: ${data.payment_status}`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Subscription verification failed");
      } finally {
        setPolling(false);
        setSearchParams({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pro Pass redirect handler: ?pro_session_id=<sid>
  useEffect(() => {
    const psid = searchParams.get("pro_session_id");
    if (!psid) return;
    setPolling(true);
    (async () => {
      try {
        const { data } = await api.get(`/billing/pro/status/${psid}`);
        if (data.payment_status === "paid") {
          toast.success("Pro activated — enjoy the lower fees");
          await refresh();
          loadHistory();
        } else {
          toast.error(`Pro payment status: ${data.payment_status}`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Pro verification failed");
      } finally {
        setPolling(false);
        setSearchParams({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buyStripe = async (pack) => {
    setBusyId(`stripe-${pack.id}`);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pack.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe checkout failed");
      setBusyId(null);
    }
  };

  const buyPaypal = async (pack) => {
    setBusyId(`paypal-${pack.id}`);
    try {
      const { data } = await api.post("/billing/paypal/create-order", {
        package_id: pack.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal order failed");
      setBusyId(null);
    }
  };

  const buyPaystack = async (pack) => {
    setBusyId(`paystack-${pack.id}`);
    try {
      const { data } = await api.post("/billing/paystack/initialize", {
        package_id: pack.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paystack init failed");
      setBusyId(null);
    }
  };

  const buy = (pack) =>
    processor === "paypal"
      ? buyPaypal(pack)
      : processor === "paystack"
      ? buyPaystack(pack)
      : buyStripe(pack);

  const buyPro = async () => {
    setBusyId("pro");
    try {
      const { data } = await api.post("/billing/pro/subscribe", {
        origin_url: window.location.origin,
        plan: proPlan,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Pro subscription");
      setBusyId(null);
    }
  };

  const cancelPro = async () => {
    if (!window.confirm("Cancel auto-renew? You keep Pro until the end of the current period.")) return;
    setBusyId("pro-cancel");
    try {
      const { data } = await api.post("/billing/pro/cancel");
      toast.success(`Auto-renew off — Pro stays active until ${data.active_until ? new Date(data.active_until).toLocaleDateString() : "period end"}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="billing-page">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">// billing</div>
          <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">Top up</h1>
        </div>
        <div className="border border-neo-border bg-neo-surface p-4" data-testid="billing-current-balance">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Current balance</div>
          <div className="mt-2 font-mono text-3xl font-bold tracking-tighter">{fmtUSDExact(user?.cash || 0)}</div>
        </div>
      </header>

      {/* Pro plan */}
      <section
        className={`mb-8 border p-6 ${user?.is_pro ? "border-[#00FF66]/50 bg-[#00FF66]/5" : "border-neo-border bg-neo-surface"}`}
        data-testid="billing-pro-card"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 border flex items-center justify-center ${user?.is_pro ? "border-[#00FF66] text-[#00FF66]" : "border-neo-border text-neutral-300"}`}>
              <Zap className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">membership</div>
              <h2 className="mt-1 text-xl font-black tracking-tight uppercase" data-testid="pro-status-label">
                {user?.on_trial
                  ? "Pro trial active"
                  : user?.is_pro
                  ? "Neo.Invest Pro — active"
                  : "Upgrade to Pro"}
              </h2>
              <p className="mt-1 text-xs text-neutral-400 max-w-md">
                Pro cuts your trading fee to{" "}
                <span className="text-white">{user?.fee_config?.pro_percent ?? 0.75}%</span> on every
                buy & sell (paper and real). Real trading is open to everyone — Pro just makes it
                cheaper.
                {user?.is_pro && user?.pro_until && (
                  <span className={`block mt-1 ${user?.on_trial ? "text-amber-400" : "text-[#00FF66]"}`}>
                    {user?.on_trial ? "Free trial ends " : "Renews/expires "}
                    {new Date(user.pro_until).toLocaleDateString()}
                    {user?.on_trial ? " — subscribe to keep the lower fee" : ""}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="text-right">
            {(() => {
              const monthly = user?.pro_price || 15;
              const annual = user?.pro_annual_price || 149;
              const isAnnual = proPlan === "annual";
              const monthsFree = Math.max(0, Math.round((monthly * 12 - annual) / monthly));
              const showToggle = !(user?.has_subscription && !user?.sub_cancel_at_period_end);
              return (
                <>
                  {showToggle && (
                    <div
                      className="mb-3 inline-flex border border-neo-border bg-neo-bg p-1"
                      data-testid="pro-plan-toggle"
                    >
                      <button
                        data-testid="pro-plan-monthly"
                        onClick={() => setProPlan("monthly")}
                        className={`px-3 py-1.5 font-mono uppercase tracking-widest text-[10px] transition-colors ${
                          !isAnnual ? "bg-white text-black" : "text-neutral-400 hover:bg-white/5"
                        }`}
                      >
                        Monthly
                      </button>
                      <button
                        data-testid="pro-plan-annual"
                        onClick={() => setProPlan("annual")}
                        className={`px-3 py-1.5 font-mono uppercase tracking-widest text-[10px] transition-colors inline-flex items-center gap-1.5 ${
                          isAnnual ? "bg-[#00FF66] text-black" : "text-neutral-400 hover:bg-white/5"
                        }`}
                      >
                        Yearly
                        {monthsFree > 0 && (
                          <span className={`text-[8px] px-1 py-0.5 border ${isAnnual ? "border-black/30" : "border-[#00FF66]/50 text-[#00FF66]"}`}>
                            {monthsFree} mo free
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                  <div className="font-mono text-3xl font-bold tracking-tighter" data-testid="pro-price-display">
                    {fmtUSDExact(isAnnual ? annual : monthly)}
                    <span className="text-sm text-neutral-500">{isAnnual ? "/yr" : "/mo"}</span>
                  </div>
                  {showToggle && isAnnual && (
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#00FF66]">
                      ≈ {fmtUSDExact(annual / 12)}/mo · billed yearly
                    </div>
                  )}
                </>
              );
            })()}
            {user?.has_subscription && !user?.sub_cancel_at_period_end ? (
              <button
                data-testid="pro-cancel-btn"
                onClick={cancelPro}
                disabled={busyId === "pro-cancel"}
                className="mt-2 px-6 py-3 font-mono uppercase tracking-widest text-xs border border-neo-border text-neutral-300 hover:bg-white/5 transition-colors disabled:opacity-40 inline-flex items-center gap-2"
              >
                {busyId === "pro-cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                Cancel auto-renew
              </button>
            ) : (
              <button
                data-testid="pro-subscribe-btn"
                onClick={buyPro}
                disabled={busyId === "pro"}
                className={`mt-2 px-6 py-3 font-mono uppercase tracking-widest text-xs border transition-colors disabled:opacity-40 inline-flex items-center gap-2 ${
                  user?.is_pro && !user?.on_trial
                    ? "border-neo-border text-neutral-300 hover:bg-white/5"
                    : "bg-[#00FF66] text-black border-[#00FF66] hover:opacity-90"
                }`}
              >
                {busyId === "pro" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                {user?.on_trial
                  ? `Subscribe ${proPlan === "annual" ? "yearly" : "monthly"} — keep Pro`
                  : user?.is_pro
                  ? "Resubscribe"
                  : `Go Pro · ${proPlan === "annual" ? "yearly" : "monthly"} auto-renew`}
              </button>
            )}
            {user?.sub_cancel_at_period_end && (
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-amber-400">
                Auto-renew off · ends {user?.pro_until ? new Date(user.pro_until).toLocaleDateString() : "period end"}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Processor toggle */}
      <div className="mb-8 border border-neo-border bg-neo-surface p-1 inline-flex" data-testid="billing-processor-toggle">
        <button
          data-testid="processor-stripe"
          onClick={() => setProcessor("stripe")}
          className={`px-4 py-2 font-mono uppercase tracking-widest text-[11px] flex items-center gap-2 transition-colors ${
            processor === "stripe" ? "bg-white text-black" : "text-neutral-400 hover:bg-white/5"
          }`}
        >
          <CreditCard className="w-3.5 h-3.5" strokeWidth={1.5} />
          Stripe
        </button>
        <button
          data-testid="processor-paypal"
          onClick={() => setProcessor("paypal")}
          disabled={!paypalCfg.enabled}
          title={paypalCfg.enabled ? "" : "PayPal not configured — add keys in Providers"}
          className={`px-4 py-2 font-mono uppercase tracking-widest text-[11px] flex items-center gap-2 transition-colors ${
            processor === "paypal" ? "bg-white text-black" : "text-neutral-400 hover:bg-white/5"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} />
          PayPal
          {paypalCfg.enabled && (
            <span className="ml-1 text-[9px] uppercase tracking-widest border border-current px-1 py-0.5">
              {paypalCfg.mode}
            </span>
          )}
          {!paypalCfg.enabled && (
            <span className="ml-1 text-[9px] uppercase tracking-widest border border-neutral-600 px-1 py-0.5">
              off
            </span>
          )}
        </button>
        <button
          data-testid="processor-paystack"
          onClick={() => setProcessor("paystack")}
          disabled={!paystackCfg.enabled}
          title={paystackCfg.enabled ? "" : "Paystack not configured — add keys in Providers"}
          className={`px-4 py-2 font-mono uppercase tracking-widest text-[11px] flex items-center gap-2 transition-colors ${
            processor === "paystack" ? "bg-white text-black" : "text-neutral-400 hover:bg-white/5"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} />
          Paystack
          {paystackCfg.enabled ? (
            <span className="ml-1 text-[9px] uppercase tracking-widest border border-current px-1 py-0.5">
              {paystackCfg.currency}
            </span>
          ) : (
            <span className="ml-1 text-[9px] uppercase tracking-widest border border-neutral-600 px-1 py-0.5">
              off
            </span>
          )}
        </button>
      </div>

      {polling && (
        <div className="mb-6 border border-neo-border bg-neo-surface p-4 flex items-center gap-3 font-mono text-xs uppercase tracking-widest" data-testid="billing-polling">
          <Loader2 className="w-4 h-4 animate-spin" /> Verifying payment…
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10" data-testid="billing-packs">
        {packs.map((p) => (
          <div
            key={p.id}
            data-testid={`billing-pack-${p.id}`}
            className={`border p-6 flex flex-col ${
              p.id === "pro" ? "border-white bg-white/5" : "border-neo-border bg-neo-surface"
            }`}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">{p.label}</div>
            <div className="mt-6 font-mono text-4xl font-black tracking-tighter">${p.amount.toFixed(0)}</div>
            <div className="mt-2 text-xs text-neutral-500">
              {p.bonus > 0 ? `+ $${p.bonus} bonus credit` : "instant credit"}
            </div>
            <div className="mt-6 pt-4 border-t border-neo-border text-[10px] text-neutral-500 uppercase tracking-widest">
              adds ${(p.amount + p.bonus).toFixed(2)} to balance
            </div>
            <button
              data-testid={`billing-buy-${p.id}`}
              onClick={() => buy(p)}
              disabled={busyId === `${processor}-${p.id}` || (processor === "paypal" && !paypalCfg.enabled) || (processor === "paystack" && !paystackCfg.enabled)}
              className={`mt-6 py-3 font-mono uppercase tracking-widest text-xs border ${
                p.id === "pro"
                  ? "bg-white text-black border-white hover-invert"
                  : "border-neo-border hover:bg-white/5"
              } disabled:opacity-60`}
            >
              {busyId === `${processor}-${p.id}` ? "Redirecting…" : `Buy via ${processor}`}
            </button>
          </div>
        ))}
      </section>

      <section className="border border-neo-border bg-neo-surface" data-testid="billing-history-table">
        <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          // payment_history
        </div>
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <div className="col-span-3">Date</div>
          <div className="col-span-2">Kind</div>
          <div className="col-span-2">Processor</div>
          <div className="col-span-1">Pack</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1 text-right">Bonus</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        <div className="divide-y divide-neo-border font-mono text-sm max-h-[50vh] overflow-y-auto">
          {history.length === 0 && (
            <div className="p-8 text-center text-neutral-500 font-mono text-xs">No payments yet</div>
          )}
          {history.map((h) => (
            <div key={h.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center">
              <div className="col-span-3 text-neutral-500 text-xs">{new Date(h.created_at).toLocaleString()}</div>
              <div className="col-span-2 uppercase text-neutral-300 text-xs">{h.kind?.replace("_", " ")}</div>
              <div className="col-span-2 uppercase text-neutral-400 text-xs">{h.processor || "—"}</div>
              <div className="col-span-1 uppercase text-neutral-400 text-xs">{h.package_id || "—"}</div>
              <div className="col-span-1 text-right text-white">{fmtUSDExact(h.amount)}</div>
              <div className="col-span-1 text-right text-neutral-400">{h.bonus ? fmtUSDExact(h.bonus) : "—"}</div>
              <div className="col-span-2 text-right">
                <StatusBadge status={h.payment_status} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "paid") {
    return (
      <span className="inline-flex items-center gap-1 border border-[#00FF66]/30 bg-[#00FF66]/10 text-[#00FF66] px-2 py-0.5 text-[10px] uppercase tracking-widest">
        <Check className="w-3 h-3" /> paid
      </span>
    );
  }
  if (status === "unpaid") {
    return (
      <span className="inline-flex items-center gap-1 border border-neo-border text-neutral-400 px-2 py-0.5 text-[10px] uppercase tracking-widest">
        pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 border border-[#FF3366]/30 bg-[#FF3366]/10 text-[#FF3366] px-2 py-0.5 text-[10px] uppercase tracking-widest">
      <X className="w-3 h-3" /> {status || "n/a"}
    </span>
  );
}
